import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Obsidian Edge Template - Bold dark header with professional contrast
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 0,
  },
  // Header with obsidian black background - compact height
  headerContainer: {
    backgroundColor: "#1a1a1a",
    paddingHorizontal: 40,
    paddingTop: 10,
    paddingBottom: 10,
    marginTop: -30, // Offset the page paddingTop so header starts at top
    marginHorizontal: 0,
  },
  // Name - white text on dark background
  name: {
    fontSize: 24,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 25,
    letterSpacing: 0.5,
  },
  // Contact info row - all items on one line
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
  },
  contactIcon: {
    width: 11,
    height: 11,
    marginRight: 5,
  },
  contactText: {
    fontSize: 9,
    color: "#e0e0e0",
  },
  // Main content
  mainContent: {
    paddingHorizontal: 40,
    paddingTop: 10,
  },
  // Section styling with icon
  sectionContainer: {
    marginBottom: 16,
  },
  sectionTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
    paddingBottom: 5,
  },
  sectionIconBox: {
    width: 18,
    height: 18,
    backgroundColor: "#1a1a1a",
    borderRadius: 3,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  sectionIcon: {
    width: 10,
    height: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#1a1a1a",
    textTransform: "capitalize",
    letterSpacing: 0.5,
  },
  // Profile/Summary section
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#444444",
    textAlign: "justify",
  },
  // Experience items
  experienceItem: {
    marginBottom: 12,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 3,
  },
  companyTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  dateLocation: {
    fontSize: 9,
    color: "#666666",
    textAlign: "right",
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 2,
    paddingLeft: 10,
    lineHeight: 1.5,
    color: "#444444",
  },
  // Education items
  educationItem: {
    marginBottom: 10,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  school: {
    fontSize: 9,
    color: "#666666",
    marginTop: 1,
  },
  educationDescription: {
    fontSize: 9,
    lineHeight: 1.5,
    color: "#444444",
    marginTop: 3,
  },
  // Skills section - categorized display
  skillsContainer: {
    flexDirection: "column",
  },
  skillCategory: {
    flexDirection: "row",
    marginBottom: 3,
    flexWrap: "wrap",
  },
  skillCategoryLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
    width: 110,
  },
  skillCategoryText: {
    fontSize: 10,
    color: "#444444",
    flex: 1,
  },
  skillItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "33.33%",
    marginBottom: 3,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 10,
    color: "#1a1a1a",
    marginRight: 4,
  },
  skillText: {
    fontSize: 10,
    color: "#1a1a1a",
    flex: 1,
  },
  // Languages section
  languageItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  languageName: {
    fontSize: 10,
    color: "#1a1a1a",
    width: 80,
  },
  languageLevel: {
    fontSize: 9,
    color: "#666666",
  },
  // Certifications
  certificationItem: {
    marginBottom: 6,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  certAuthority: {
    fontSize: 9,
    color: "#666666",
  },
  // Projects
  projectItem: {
    marginBottom: 8,
  },
  projectTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  projectTech: {
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 1,
  },
});

// Professional SVG icon paths
const contactIcons = {
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  github: "M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z",
};

// Section icons SVG paths - white icons on dark background
const sectionIcons = {
  profile: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z",
  experience: "M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z",
  education: "M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z",
  skills: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",
  projects: "M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6 10H6v-2h8v2zm4-4H6v-2h12v2z",
  certifications: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z",
  languages: "M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z",
  achievements: "M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z",
};

const ObsidianEdgeTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
  } = resumeData || {};

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 1.8 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.9 };
      customStyleOverrides.companyTitle = { ...baseStyles.companyTitle, fontSize: fontSize * 0.75 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.75 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = { ...baseStyles.sectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.headerContainer = {
        ...(customStyleOverrides.headerContainer || baseStyles.headerContainer),
        backgroundColor: titleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Default section titles
  const defaultTitles = {
    summary: 'Profile', workExperience: 'Experience', education: 'Education', skills: 'Skills',
    projects: 'Projects', certifications: 'Certifications', achievements: 'Achievements',
    languages: 'Languages', awards: 'Awards', volunteering: 'Volunteering', interests: 'Interests',
    industryExpertise: 'Industry Expertise', publications: 'Publications', references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Default section order
  const defaultSectionOrder = ["summary", "workExperience", "education", "skills", "projects", "certifications", "achievements", "languages", "awards", "volunteering", "interests", "industryExpertise", "publications", "references"];

  // Build final section order - include custom sections
  const customSectionIds = customSections.filter(cs => cs.id && cs.title && cs.items?.length > 0).map(cs => cs.id);
  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [...baseSectionOrder, ...customSectionIds.filter(id => !baseSectionOrder.includes(id))];

  // Combine all skills for display - support categorized display
  const skillsData = (() => {
    if (Array.isArray(skills)) return { list: skills };
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return { list: skills.list };

    // Check for categorized skills
    const categories = {};
    if (skills.technical?.length > 0) categories.Frontend = skills.technical;
    if (skills.soft?.length > 0) categories["Soft Skills"] = skills.soft;
    if (skills.tools?.length > 0) categories.Tools = skills.tools;
    if (skills.other?.length > 0) categories.Other = skills.other;

    if (skills.categorized) {
      if (skills.categorized.technical?.length > 0) categories.Frontend = skills.categorized.technical;
      if (skills.categorized.backend?.length > 0) categories.Backend = skills.categorized.backend;
      if (skills.categorized.cloud?.length > 0) categories["Cloud & DevOps"] = skills.categorized.cloud;
      if (skills.categorized.architecture?.length > 0) categories["Architecture & Tools"] = skills.categorized.architecture;
      if (skills.categorized.testing?.length > 0) categories["Testing & Quality"] = skills.categorized.testing;
      if (skills.categorized.soft?.length > 0) categories["Soft Skills"] = skills.categorized.soft;
    }

    if (Object.keys(categories).length > 0) return { categories };

    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    return { list: combinedSkills };
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest name from string or object
  const getInterestName = (interest) => {
    if (typeof interest === "string") return interest;
    if (interest && typeof interest === "object") return interest.name || interest.title || "";
    return "";
  };

  // Render contact icon
  const renderContactIcon = (iconPath) => (
    <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#e0e0e0" />
    </Svg>
  );

  // Render section icon - white icon on dark background
  const renderSectionIcon = (iconPath) => (
    <View style={mergedStyles.sectionIconBox}>
      <Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24">
        <Path d={iconPath} fill="#ffffff" />
      </Svg>
    </View>
  );

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.profile)}
            <Text style={mergedStyles.sectionTitle}>Profile</Text>
          </View>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.experience)}
            <Text style={mergedStyles.sectionTitle}>Professional Experience</Text>
          </View>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.companyTitle}>
                    {job.jobTitle && <Text style={{ fontWeight: 700 }}>{job.jobTitle}, </Text>}
                    {job.company}
                  </Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.dateLocation}>
                    {formatDate(job.startDate)} –{" "}
                    {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                      ? "Present"
                      : formatDate(job.endDate)}
                  </Text>
                </View>
              </View>
              {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
            </View>
          ))}
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.education)}
            <Text style={mergedStyles.sectionTitle}>Education</Text>
          </View>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>
                    {edu.degree}
                    {edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.school}>{edu.school}</Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.dateLocation}>
                    {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                </View>
              </View>
              {edu.description && (
                <Text style={mergedStyles.educationDescription}>{parseHtmlToText(edu.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    skills: () => {
      if (skillsData.categories) {
        return (
          <View style={mergedStyles.sectionContainer} wrap={false}>
            <View style={mergedStyles.sectionTitleContainer}>
              {renderSectionIcon(sectionIcons.skills)}
              <Text style={mergedStyles.sectionTitle}>Skills</Text>
            </View>
            <View style={mergedStyles.skillsContainer}>
              {Object.entries(skillsData.categories).map(([category, categorySkills], index) => (
                <View key={index} style={mergedStyles.skillCategory}>
                  <Text style={mergedStyles.skillCategoryLabel}>{category}:</Text>
                  <Text style={mergedStyles.skillCategoryText}>
                    {categorySkills.map(getSkillName).join(", ")}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        );
      }

      if (skillsData.list?.length > 0) {
        return (
          <View style={mergedStyles.sectionContainer} wrap={false}>
            <View style={mergedStyles.sectionTitleContainer}>
              {renderSectionIcon(sectionIcons.skills)}
              <Text style={mergedStyles.sectionTitle}>Skills</Text>
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              {skillsData.list.slice(0, 15).map((skill, index) => (
                <View key={index} style={mergedStyles.skillItem}>
                  <Text style={mergedStyles.skillBullet}>•</Text>
                  <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
                </View>
              ))}
            </View>
          </View>
        );
      }

      return null;
    },

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.projects)}
            <Text style={mergedStyles.sectionTitle}>Projects</Text>
          </View>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>}
              {project.description && (
                <View style={{ marginTop: 3 }}>{renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}</View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.certifications)}
            <Text style={mergedStyles.sectionTitle}>Certifications</Text>
          </View>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <Text style={mergedStyles.certName}>{cert.name}</Text>
              <Text style={mergedStyles.certAuthority}>
                {cert.authority || cert.issuer}
                {cert.date && ` • ${formatDate(cert.date)}`}
              </Text>
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages &&
      languages.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.languages)}
            <Text style={mergedStyles.sectionTitle}>Languages</Text>
          </View>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.languageItem}>
              <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
              {lang.proficiency && <Text style={mergedStyles.languageLevel}>– {lang.proficiency}</Text>}
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            {renderSectionIcon(sectionIcons.achievements)}
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
          </View>
          {achievements.map((achievement, index) => (
            <View key={index} style={{ marginBottom: 5 }}>
              {achievement.description && (
                <Text style={mergedStyles.summaryText}>{parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
        </View>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{award.title || award.name}</Text>
            <Text style={mergedStyles.certAuthority}>{award.issuer}{award.date && ` • ${formatDate(award.date)}`}</Text>
          </View>
        ))}
      </View>
    ),

    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
        </View>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.companyTitle}>{vol.role}</Text>
              {(vol.startDate || vol.endDate) && <Text style={mergedStyles.dateRange}>{formatDate(vol.startDate)} – {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}</Text>}
            </View>
            <Text style={mergedStyles.companyName}>{vol.organization}</Text>
            {vol.description && <View style={{ marginTop: 4 }}>{renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}</View>}
          </View>
        ))}
      </View>
    ),

    interests: () => interests.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        </View>
        <View style={mergedStyles.skillsContainer}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillPill}>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        </View>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{exp.name}</Text>
            <Text style={mergedStyles.certAuthority}>{exp.proficiency}{exp.yearsOfExperience && ` • ${exp.yearsOfExperience} years`}</Text>
          </View>
        ))}
      </View>
    ),

    publications: () => publications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
        </View>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{pub.title}</Text>
            <Text style={mergedStyles.certAuthority}>{pub.publisher}{pub.date && ` • ${formatDate(pub.date)}`}</Text>
          </View>
        ))}
      </View>
    ),

    references: () => references.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="#ffffff" /></Svg></View>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
        </View>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{ref.name}</Text>
            <Text style={mergedStyles.certAuthority}>{ref.position}{ref.company && ` at ${ref.company}`}{ref.email && ` • ${ref.email}`}</Text>
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <View style={mergedStyles.sectionTitleContainer}>
              <View style={mergedStyles.sectionIconBox}><Svg style={mergedStyles.sectionIcon} viewBox="0 0 24 24"><Path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6z" fill="#ffffff" /></Svg></View>
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={{ marginBottom: 5 }}>
                {item.content && <Text style={mergedStyles.summaryText}>{parseHtmlToText(item.content)}</Text>}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section - Compact Dark Obsidian Background */}
        <View style={mergedStyles.headerContainer}>
          {/* Name */}
          <Text style={mergedStyles.name}>
            {personalInfo.firstName} {personalInfo.lastName}
          </Text>

          {/* Contact Info - Single Row with Icons */}
          <View style={mergedStyles.contactRow}>
            {/* Email */}
            {personalInfo.email && (
              <View style={mergedStyles.contactItem}>
                {renderContactIcon(contactIcons.email)}
                <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
              </View>
            )}

            {/* Phone */}
            {personalInfo.phone && (
              <View style={mergedStyles.contactItem}>
                {renderContactIcon(contactIcons.phone)}
                <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
              </View>
            )}

            {/* Location */}
            {personalInfo.location && (
              <View style={mergedStyles.contactItem}>
                {renderContactIcon(contactIcons.location)}
                <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
              </View>
            )}

            {/* LinkedIn or GitHub */}
            {(personalInfo.linkedin || personalInfo.github) && (
              <View style={mergedStyles.contactItem}>
                {renderContactIcon(contactIcons.github)}
                <Text style={mergedStyles.contactText}>{personalInfo.linkedin || personalInfo.github}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content */}
        <View style={mergedStyles.mainContent}>
          {finalSectionOrder.map((sectionId, index) => {
            const SectionComponent = sectionComponents[sectionId];
            const content = SectionComponent ? SectionComponent() : null;
            return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default ObsidianEdgeTemplate;
